import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, ShieldCheck, KeyRound } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not send reset link. Please try again.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-tge-paper">
      <main className="flex-grow flex items-center justify-center px-4 pt-[100px] pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 0.9, 0.32, 1] }}
          className="w-full max-w-md bg-white rounded-3xl shadow-[0_20px_60px_rgba(27,42,107,0.12)] overflow-hidden"
          data-testid="forgot-password-card"
        >
          <div className="p-8 sm:p-10">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-xs text-tge-muted hover:text-tge-navy transition"
              data-testid="forgot-password-back"
            >
              <ArrowLeft size={14} /> Back to all portals
            </Link>

            <div className="w-14 h-14 rounded-2xl bg-tge-navy flex items-center justify-center mt-6 shadow-lg">
              <KeyRound className="text-white" size={26} strokeWidth={1.8} />
            </div>

            <h1 className="font-serif-display text-3xl text-tge-navy mt-6">Forgot password?</h1>
            <p className="text-tge-muted text-sm mt-2 leading-relaxed">
              No worries. Enter the email tied to your account and we'll send you a secure link to set a new password.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-7 rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
                data-testid="forgot-password-success"
              >
                <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                  <Mail size={16} /> Check your inbox
                </div>
                <p className="text-sm text-emerald-800/80 mt-2 leading-relaxed">
                  If an account exists for <strong>{email}</strong>, a password reset link has been sent.
                  The link expires in 60 minutes and can only be used once.
                </p>
                <p className="text-xs text-emerald-800/60 mt-3">
                  Didn't get it? Check spam, or
                  <button
                    type="button"
                    onClick={() => { setSubmitted(false); }}
                    className="ml-1 underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900"
                    data-testid="forgot-password-try-again"
                  >
                    try a different email
                  </button>.
                </p>
                <Link
                  to="/login"
                  className="mt-5 inline-flex items-center justify-center w-full h-11 rounded-xl bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold text-sm transition"
                  data-testid="forgot-password-back-to-login"
                >
                  Back to sign in
                </Link>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm"
                    data-testid="forgot-password-error"
                  >
                    {error}
                  </motion.div>
                )}

                <div>
                  <label
                    className="block text-[12px] font-semibold uppercase tracking-wider text-tge-muted mb-1.5"
                    htmlFor="email"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="forgot-password-email"
                    className="w-full h-12 px-4 rounded-xl bg-tge-cream-soft border border-transparent focus:border-tge-gold focus:bg-white focus:outline-none focus:ring-4 focus:ring-tge-gold/15 transition placeholder:text-tge-muted/50 text-[15px]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  data-testid="forgot-password-submit"
                  className="w-full h-12 rounded-xl bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold text-[15px] transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-tge-navy/20 inline-flex items-center justify-center gap-2"
                >
                  {loading ? 'Sending link…' : 'Send reset link'}
                  {!loading && <Mail size={16} className="opacity-80" />}
                </button>

                <div className="flex items-center gap-2 text-[11px] text-tge-muted/70 pt-1">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  <span>Secure, single-use link · expires in 60 minutes</span>
                </div>

                <p className="text-center text-sm text-tge-muted pt-4 border-t border-tge-cream">
                  Remembered it?{' '}
                  <Link
                    to="/login"
                    className="text-tge-navy font-semibold underline decoration-tge-gold/60 underline-offset-4 hover:decoration-tge-gold"
                    data-testid="forgot-password-signin-link"
                  >
                    Back to sign in
                  </Link>
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
